import React from "react";
import {
  Box,
  Typography,
  Link,
  Button,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formattedAmountWithoutAed } from "../../../../utils/CommonFunction";
import { renderCurrencyIconColored } from "../../../../utils/CurrencyUtils";
import PdfIconSvg from "../../../../assets/svg/SmallPdfIcon.svg";
import { paymentStatusStyleArray } from "../../config";
import moment from "moment";

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;

  if (text.toLowerCase() === "created") {
    return "-";
  }

  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

const uploadBtnStyle = {
  backgroundColor: "#4D6BF6",
  borderRadius: "12px",
  fontSize: "14px",
  fontWeight: 500,
  boxShadow: "none",
  color: "#fff",
  width: "100%",
  textTransform: "none",
  "&:hover": { backgroundColor: "#4D6BF6" },
};

const deleteBtnStyle = {
  backgroundColor: "#fff",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 500,
  color: "#D64B64",
  border: "1px solid #D7D8D9",
  width: "100%",
  textTransform: "none",
};

const MobileView = ({ details, handleDeleteFile, handleFileSelect }) => {
  if (!details?.length) {
    return (
      <Box sx={{ textAlign: "center", padding: "4vh 2vw", color: "#949DAA" }}>
        No payments yet
      </Box>
    );
  }

  const flatScopes = [];
  details.forEach((project) => {
    (project.scopes || []).forEach((scope) => {
      flatScopes.push({ project, scope });
    });
  });

  return (
    <div style={mobileStyles.container}>
      {flatScopes.map(({ project, scope }, scopeIndex) => (
        <Accordion
          key={scope.scopeId}
          defaultExpanded={scopeIndex === 0}
          sx={mobileStyles.accordion}
          disableGutters
          elevation={0}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={mobileStyles.accordionSummary}
          >
            <div style={mobileStyles.scopeHeader}>
              <Avatar
                src={scope.contractor?.profileUrl}
                sx={{ width: 36, height: 36 }}
              />
              <div style={mobileStyles.scopeHeaderText}>
                <Typography sx={mobileStyles.scopeName}>
                  {scope.scopeName}
                </Typography>
                <Typography sx={mobileStyles.projectName}>
                  {project.projectName}
                </Typography>
              </div>
            </div>
          </AccordionSummary>

          <AccordionDetails sx={mobileStyles.accordionDetails}>
            {!scope.payouts?.length ? (
              <div style={mobileStyles.emptyPayouts}>No payouts</div>
            ) : (
              <div style={mobileStyles.payoutsList}>
                {scope.payouts.map((item) => {
                  const metaData = item?.metadata
                    ? JSON.parse(item?.metadata)
                    : null;

                  const invoiceData = item?.invoice
                    ? item.invoice
                    : item?.invoiceUrl
                    ? JSON.parse(item.invoiceUrl)
                    : null;

                  let invoiceStatus = null;
                  if (invoiceData) {
                    if (invoiceData.status) {
                      invoiceStatus = invoiceData.status;
                    } else {
                      invoiceStatus = invoiceData.approve
                        ? "APPROVED"
                        : "IN_REVIEW";
                    }
                  }

                  const fileUrl = metaData?.file?.fileUrl;

                  return (
                    <div key={item.id} style={mobileStyles.payoutCard}>
                      {/* HEADER: Payment name + receipt */}
                      <div style={mobileStyles.cardHeader}>
                        <div style={mobileStyles.cardHeaderText}>
                          <Typography sx={mobileStyles.fieldLabel}>
                            Payment
                          </Typography>
                          <Typography sx={mobileStyles.payoutName}>
                            {item?.payoutName}
                          </Typography>
                        </div>

                        {fileUrl && (
                          <Link
                            href={fileUrl}
                            target="_blank"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <img
                              src={PdfIconSvg}
                              alt="Receipt"
                              style={mobileStyles.pdfIcon}
                            />
                          </Link>
                        )}
                      </div>

                      {/* GRID: Due Date / Status / Settled / Amount */}
                      <div style={mobileStyles.fieldGrid}>
                        <div style={mobileStyles.fieldItem}>
                          <Typography sx={mobileStyles.fieldLabel}>
                            Due Date
                          </Typography>
                          <Typography sx={mobileStyles.fieldValue}>
                            {item?.dueDate
                              ? moment(item?.dueDate).format("MMM DD, YYYY")
                              : "-"}
                          </Typography>
                        </div>

                        <div style={mobileStyles.fieldItem}>
                          <Typography sx={mobileStyles.fieldLabel}>
                            Status
                          </Typography>
                          <div style={mobileStyles.statusContainer}>
                            <div
                              style={{
                                ...mobileStyles.statusDot,
                                backgroundColor:
                                  paymentStatusStyleArray.find(
                                    (style) => style.status === item?.status
                                  )?.backgroundColor || "#FFAD5C",
                              }}
                            />
                            <Typography sx={mobileStyles.statusText}>
                              {getStatusDisplayText(item?.status)}
                            </Typography>
                          </div>
                        </div>

                        <div style={mobileStyles.fieldItem}>
                          <Typography sx={mobileStyles.fieldLabel}>
                            Settled On
                          </Typography>
                          <Typography sx={mobileStyles.fieldValue}>
                            {item?.releaseDate
                              ? moment(item?.releaseDate).format(
                                  "MMM DD, YYYY"
                                )
                              : item?.processedOn
                              ? moment(item?.processedOn).format(
                                  "MMM DD, YYYY"
                                )
                              : "-"}
                          </Typography>
                        </div>

                        <div style={mobileStyles.fieldItem}>
                          <Typography sx={mobileStyles.fieldLabel}>
                            Amount Due
                          </Typography>
                          <Typography
                            component="span"
                            sx={mobileStyles.amountValue}
                          >
                            {renderCurrencyIconColored("small")}
                            {formattedAmountWithoutAed(item?.amount)}
                          </Typography>
                        </div>
                      </div>

                      {/* INVOICE actions */}
                      {item.amount !== 0 && (
                        <div style={{ marginTop: 4 }}>
                          {!invoiceData?.url ? (
                            item.status === "DUE" && (
                              <Button
                                variant="contained"
                                component="label"
                                sx={uploadBtnStyle}
                              >
                                Upload Invoice
                                <input
                                  id={`pdf-upload-${item.id}`}
                                  type="file"
                                  accept="application/pdf"
                                  hidden
                                  onChange={(e) =>
                                    handleFileSelect(e, item.id)
                                  }
                                />
                              </Button>
                            )
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                }}
                              >
                                <Link
                                  href={invoiceData.url}
                                  target="_blank"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                  title="Download Invoice"
                                >
                                  <img
                                    src={PdfIconSvg}
                                    alt="Invoice"
                                    style={mobileStyles.pdfIcon}
                                  />
                                </Link>

                                {invoiceStatus === "IN_REVIEW" && (
                                  <Box
                                    sx={{
                                      color: "#E6A23C",
                                      fontWeight: 500,
                                      fontSize: 13,
                                    }}
                                  >
                                    In Review
                                  </Box>
                                )}

                                {invoiceStatus === "APPROVED" && (
                                  <Box
                                    sx={{
                                      color: "#28a745",
                                      fontWeight: 500,
                                      fontSize: 13,
                                    }}
                                  >
                                    Approved
                                  </Box>
                                )}

                                {invoiceStatus === "REJECTED" && (
                                  <Box
                                    sx={{
                                      color: "#D64B64",
                                      fontWeight: 500,
                                      fontSize: 13,
                                    }}
                                  >
                                    Rejected
                                  </Box>
                                )}
                              </div>

                              {invoiceStatus === "REJECTED" && (
                                <Button
                                  variant="outlined"
                                  onClick={() =>
                                    handleDeleteFile(
                                      invoiceData.blobFileId,
                                      item.id
                                    )
                                  }
                                  sx={deleteBtnStyle}
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};

const mobileStyles = {
  container: {
    padding: "16px 0",
    backgroundColor: "transparent",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  accordion: {
    borderRadius: "12px !important",
    border: "1px solid #E5E7EB",
    background: "#fff",
    boxShadow: "none",
    "&:before": { display: "none" },
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  accordionSummary: {
    background: "#F7F8FA",
    borderRadius: "12px",
    minHeight: "auto",
    padding: "8px 12px",
    "&.Mui-expanded": {
      borderRadius: "12px 12px 0 0",
      minHeight: "auto",
    },
    "& .MuiAccordionSummary-content": {
      margin: 0,
      "&.Mui-expanded": { margin: 0 },
    },
  },
  accordionDetails: {
    padding: "12px",
  },
  scopeHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
    flex: 1,
  },
  scopeHeaderText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  scopeName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#0D0D0E",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  projectName: {
    fontSize: 12,
    color: "#949DAA",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  emptyPayouts: {
    fontSize: 13,
    color: "#949DAA",
    textAlign: "center",
    padding: "12px 0",
  },
  payoutsList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  payoutCard: {
    backgroundColor: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    padding: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  },
  cardHeaderText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  payoutName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#0D0D0E",
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
  },
  fieldItem: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: "#949DAA",
    fontWeight: 500,
  },
  fieldValue: {
    fontSize: 13,
    color: "#0D0D0E",
    fontWeight: 500,
  },
  amountValue: {
    fontSize: 13,
    color: "#0D0D0E",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
  },
  statusContainer: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  statusDot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    border: "3px solid rgb(229, 229, 229)",
    flexShrink: 0,
  },
  statusText: {
    fontSize: 13,
    color: "#0D0D0E",
    fontWeight: 500,
  },
  pdfIcon: {
    width: 22,
    height: 22,
  },
};

export default MobileView;
